import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  EmailSyncResponseSchema,
  EmailSyncsResponseSchema,
} from "@forgepoint/shared";
import type { EmailSync } from "@forgepoint/shared";

interface EmailSyncState {
  isLoadingFetch: boolean;
  isLoadingCreate: boolean;
  isLoadingDelete: boolean;
  fetchError: string | null;
  createError: string | null;
  deleteError: string | null;
}

const defaultEmailSyncState: EmailSyncState = {
  isLoadingFetch: false,
  isLoadingCreate: false,
  isLoadingDelete: false,
  fetchError: null,
  createError: null,
  deleteError: null,
};

interface EmailSyncsStore {
  emailSyncs: Map<string, EmailSync>;
  emailSyncStates: Map<string, EmailSyncState>;
  globalLoadingState: {
    isLoadingFetchAll: boolean;
    fetchAllError: string | null;
  };

  fetchEmailSyncs: () => Promise<void>;
  createEmailSync: (connectionId: string) => Promise<EmailSync | null>;
  deleteEmailSync: (id: string) => Promise<boolean>;

  getEmailSyncById: (id: string) => EmailSync | undefined;
  getEmailSyncByConnectionId: (connectionId: string) => EmailSync | undefined;
  getAllEmailSyncs: () => EmailSync[];
  clearEmailSyncError: (id: string) => void;
}

export const useEmailSyncsStore = create<EmailSyncsStore>((set, get) => ({
  emailSyncs: new Map<string, EmailSync>(),
  emailSyncStates: new Map<string, EmailSyncState>(),
  globalLoadingState: {
    isLoadingFetchAll: false,
    fetchAllError: null,
  },

  fetchEmailSyncs: async () => {
    set({
      globalLoadingState: {
        isLoadingFetchAll: true,
        fetchAllError: null,
      },
    });

    try {
      const response = await fetchApi("email-syncs", "GET");

      if (response.ok) {
        const parsed = EmailSyncsResponseSchema.safeParse(response.data);

        if (parsed.success) {
          const newEmailSyncs = new Map<string, EmailSync>();

          parsed.data.emailSyncs.forEach((emailSync) => {
            newEmailSyncs.set(emailSync.id, emailSync);
          });

          set({
            emailSyncs: newEmailSyncs,
            globalLoadingState: {
              isLoadingFetchAll: false,
              fetchAllError: null,
            },
          });
          return;
        }

        captureException(parsed.error);
        set({
          globalLoadingState: {
            isLoadingFetchAll: false,
            fetchAllError: "Failed to parse email syncs",
          },
        });
        return;
      }

      const errorData = response.data as { error?: string } | undefined;
      const errorMessage = errorData?.error || "Failed to fetch email syncs";

      set({
        globalLoadingState: {
          isLoadingFetchAll: false,
          fetchAllError: errorMessage,
        },
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch email syncs";
      captureException(error as Error);

      set({
        globalLoadingState: {
          isLoadingFetchAll: false,
          fetchAllError: errorMessage,
        },
      });
    }
  },

  createEmailSync: async (connectionId: string) => {
    const currentState =
      get().emailSyncStates.get(connectionId) || defaultEmailSyncState;

    set({
      emailSyncStates: new Map(get().emailSyncStates).set(connectionId, {
        ...currentState,
        isLoadingCreate: true,
        createError: null,
      }),
    });

    try {
      const response = await fetchApi("email-syncs", "POST", {
        body: { connectionId },
      });

      if (response.ok) {
        const parsed = EmailSyncResponseSchema.safeParse(response.data);

        if (!parsed.success) {
          captureException(parsed.error);

          set({
            emailSyncStates: new Map(get().emailSyncStates).set(connectionId, {
              ...currentState,
              isLoadingCreate: false,
              createError: "Failed to parse email sync",
            }),
          });
          return null;
        }

        const newEmailSyncs = new Map(get().emailSyncs);
        newEmailSyncs.set(parsed.data.emailSync.id, parsed.data.emailSync);

        set({
          emailSyncs: newEmailSyncs,
          emailSyncStates: new Map(get().emailSyncStates).set(connectionId, {
            ...currentState,
            isLoadingCreate: false,
            createError: null,
          }),
        });

        return parsed.data.emailSync;
      }

      const errorData = response.data as { error?: string } | undefined;
      const errorMessage = errorData?.error || "Failed to create email sync";

      set({
        emailSyncStates: new Map(get().emailSyncStates).set(connectionId, {
          ...currentState,
          isLoadingCreate: false,
          createError: errorMessage,
        }),
      });

      return null;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create email sync";
      captureException(error as Error);

      set({
        emailSyncStates: new Map(get().emailSyncStates).set(connectionId, {
          ...currentState,
          isLoadingCreate: false,
          createError: errorMessage,
        }),
      });

      return null;
    }
  },

  deleteEmailSync: async (id: string) => {
    const currentState = get().emailSyncStates.get(id) || defaultEmailSyncState;

    set({
      emailSyncStates: new Map(get().emailSyncStates).set(id, {
        ...currentState,
        isLoadingDelete: true,
        deleteError: null,
      }),
    });

    try {
      const response = await fetchApi(`email-syncs/${id}`, "DELETE");

      if (response.ok) {
        const newEmailSyncs = new Map(get().emailSyncs);
        newEmailSyncs.delete(id);

        const newStates = new Map(get().emailSyncStates);
        newStates.delete(id);

        set({
          emailSyncs: newEmailSyncs,
          emailSyncStates: newStates,
        });

        return true;
      }

      const errorData = response.data as { error?: string } | undefined;
      const errorMessage = errorData?.error || "Failed to delete email sync";

      set({
        emailSyncStates: new Map(get().emailSyncStates).set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: errorMessage,
        }),
      });

      return false;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete email sync";
      captureException(error as Error);

      set({
        emailSyncStates: new Map(get().emailSyncStates).set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: errorMessage,
        }),
      });

      return false;
    }
  },

  getEmailSyncById: (id: string) => {
    return get().emailSyncs.get(id);
  },

  getEmailSyncByConnectionId: (connectionId: string) => {
    const emailSyncs = Array.from(get().emailSyncs.values());
    return emailSyncs.find((sync) => sync.connectionId === connectionId);
  },

  getAllEmailSyncs: () => {
    return Array.from(get().emailSyncs.values());
  },

  clearEmailSyncError: (id: string) => {
    const currentState = get().emailSyncStates.get(id);
    if (currentState) {
      set({
        emailSyncStates: new Map(get().emailSyncStates).set(id, {
          ...currentState,
          fetchError: null,
          createError: null,
          deleteError: null,
        }),
      });
    }
  },
}));
